import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Divider,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Business,
  AccessTime,
  Assignment,
  Assessment,
  Settings,
  Devices,
  Notifications,
  AccountCircle,
  Logout,
  Schedule,
  Policy,
  Description,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";

const drawerWidth = 280;

// Admin/Regular user menu items
const adminMenuItems = [
  { text: "Dashboard", icon: <Dashboard />, path: "/dashboard" },
  {
    text: "Workers",
    icon: <People />,
    path: "/workers",
    permission: "workers.read",
  },
  {
    text: "Contractors",
    icon: <Business />,
    path: "/contractors",
    permission: "contractors.read",
  },
  {
    text: "Attendance",
    icon: <AccessTime />,
    path: "/attendance",
    permission: "attendance.read",
  },
  {
    text: "Shift Management",
    icon: <Schedule />,
    path: "/shifts",
    permission: "attendance.read",
  },
  {
    text: "Attendance Policies",
    icon: <Policy />,
    path: "/attendance-policies",
    permission: "attendance.read",
  },
  {
    text: "Compliance",
    icon: <Assignment />,
    path: "/compliance",
    permission: "compliance.read",
  },
  {
    text: "Statutory Reports",
    icon: <Description />,
    path: "/statutory-reports",
    permission: "reports.read",
  },
  {
    text: "Roster Management",
    icon: <Schedule />,
    path: "/roster-management",
    permission: "attendance.read",
  },
  {
    text: "Work Orders",
    icon: <Assignment />,
    path: "/work-orders",
  },
  {
    text: "Reports",
    icon: <Assessment />,
    path: "/reports",
    permission: "reports.read",
  },
  {
    text: "Devices",
    icon: <Devices />,
    path: "/devices",
    permission: "devices.read",
  },
  { text: "Notifications", icon: <Notifications />, path: "/notifications" },
  {
    text: "Settings",
    icon: <Settings />,
    path: "/settings",
    permission: "settings.read",
  },
];

// Contractor-specific menu items
const contractorMenuItems = [
  { text: "Dashboard", icon: <Dashboard />, path: "/dashboard" },
  {
    text: "My Workers",
    icon: <People />,
    path: "/workers",
  },
  {
    text: "My Work Orders",
    icon: <Assignment />,
    path: "/work-orders",
  },
  {
    text: "Attendance",
    icon: <AccessTime />,
    path: "/attendance",
  },
  {
    text: "Compliance",
    icon: <Assignment />,
    path: "/compliance",
  },
  {
    text: "Statutory Reports",
    icon: <Description />,
    path: "/statutory-reports",
  },
  {
    text: "Reports",
    icon: <Assessment />,
    path: "/reports",
  },
  { text: "Notifications", icon: <Notifications />, path: "/notifications" },
  {
    text: "Profile",
    icon: <Settings />,
    path: "/profile",
  },
];

const superAdminItems = [
  {
    text: "Dashboard",
    icon: <Dashboard />,
    path: "/superadmin/dashboard",
  },
  {
    text: "Tenant Management",
    icon: <Business />,
    path: "/superadmin/tenants",
  },
  {
    text: "Settings",
    icon: <Settings />,
    path: "/settings",
  },
  {
    text: "Notifications",
    icon: <Notifications />,
    path: "/notifications",
  },
];

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { currentTenant, hasPermission } = useTenant();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate("/login");
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const getSubscriptionColor = (plan) => {
    switch (plan) {
      case "starter":
        return "default";
      case "professional":
        return "primary";
      case "enterprise":
        return "secondary";
      default:
        return "default";
    }
  };

  const drawer = (
    <div>
      <Toolbar sx={{ borderBottom: "1px solid #e0e0e0" }}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 40,
              height: 40,
              mr: 2,
              fontSize: "1.2rem",
            }}
          >
            CL
          </Avatar>
          <Box>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: 600 }}
            >
              {user?.role === "contractor_user" ||
              user?.role === "contractor_admin"
                ? "Contractor Portal"
                : user?.role === "superadmin"
                ? "CLMS Enterprise"
                : "CLMS SaaS"}
            </Typography>
            {user?.role === "contractor_user" ||
            user?.role === "contractor_admin" ? (
              <Chip
                label="CONTRACTOR"
                size="small"
                color="primary"
                sx={{ fontSize: "0.7rem", height: 20 }}
              />
            ) : currentTenant ? (
              <Chip
                label={currentTenant.subscription.plan.toUpperCase()}
                size="small"
                color={getSubscriptionColor(currentTenant.subscription.plan)}
                sx={{ fontSize: "0.7rem", height: 20 }}
              />
            ) : null}
          </Box>
        </Box>
      </Toolbar>

      <List sx={{ px: 1 }}>
        {(() => {
          // Don't show regular menu items for superadmin
          if (user?.role === "superadmin") {
            return null;
          }

          // Choose menu based on user role
          const menuItems =
            user?.role === "contractor_user" ||
            user?.role === "contractor_admin"
              ? contractorMenuItems
              : adminMenuItems;

          return menuItems.map((item) => {
            // Check permissions for admin users
            if (
              item.permission &&
              user?.role !== "contractor_user" &&
              user?.role !== "contractor_admin"
            ) {
              const [module, action] = item.permission.split(".");
              if (!hasPermission(module, action)) {
                return null;
              }
            }

            const isActive = location.pathname === item.path;

            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: 2,
                    bgcolor: isActive ? "primary.light" : "transparent",
                    color: isActive ? "primary.contrastText" : "inherit",
                    "&:hover": {
                      bgcolor: isActive ? "primary.main" : "action.hover",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? "primary.contrastText" : "inherit",
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: isActive ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          });
        })()}

        {/* Super Admin Section */}
        {user?.role === "superadmin" && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: "block",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              SUPER ADMIN
            </Typography>
            {superAdminItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      borderRadius: 2,
                      bgcolor: isActive ? "secondary.light" : "transparent",
                      color: isActive ? "secondary.contrastText" : "inherit",
                      "&:hover": {
                        bgcolor: isActive ? "secondary.main" : "action.hover",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isActive ? "secondary.contrastText" : "inherit",
                        minWidth: 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: "0.9rem",
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: "background.paper",
          color: "text.primary",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.role === "contractor_user" ||
            user?.role === "contractor_admin"
              ? `Welcome, ${user?.firstName || user?.username || "Contractor"}`
              : currentTenant
              ? currentTenant.name
              : "CLMS Enterprise"}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Badge badgeContent={4} color="error">
              <IconButton color="inherit">
                <Notifications />
              </IconButton>
            </Badge>

            <IconButton onClick={handleMenuOpen} color="inherit">
              <Avatar
                sx={{ width: 32, height: 32 }}
                src={user?.profile?.avatar}
              >
                {user?.firstName?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              onClick={() => {
                handleNavigate("/profile");
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderRight: "1px solid #e0e0e0",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: "calc(100vh - 64px)",
          bgcolor: "background.default",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
